import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

export interface ChecklistItem {
  id: string;
  label: string;
  required: boolean;
}

export interface StudioSettings {
  consent_validity_months: number;
  pause_alert_days: number;
  stock_low_threshold: number;
  checklist_items: ChecklistItem[];
}

export const STUDIO_DEFAULTS: StudioSettings = {
  consent_validity_months: 12,
  pause_alert_days: 3,
  stock_low_threshold: 5,
  checklist_items: [],
};

export function useStudioSettings() {
  const { user } = useAuth();
  const qc = useQueryClient();

  const query = useQuery<StudioSettings>({
    queryKey: ['studio_settings', user?.id],
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from('studio_settings')
        .select('*')
        .eq('owner_id', user!.id)
        .maybeSingle();
      if (!data) return { ...STUDIO_DEFAULTS };
      return {
        consent_validity_months: data.consent_validity_months ?? STUDIO_DEFAULTS.consent_validity_months,
        pause_alert_days:        data.pause_alert_days        ?? STUDIO_DEFAULTS.pause_alert_days,
        stock_low_threshold:     data.stock_low_threshold     ?? STUDIO_DEFAULTS.stock_low_threshold,
        checklist_items:         (data.checklist_items as ChecklistItem[]) ?? [],
      };
    },
    enabled: !!user,
  });

  const save = useMutation({
    mutationFn: async (updates: Partial<StudioSettings>) => {
      const { error } = await (supabase as any)
        .from('studio_settings')
        .upsert({ owner_id: user!.id, ...updates }, { onConflict: 'owner_id' });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['studio_settings'] }),
  });

  return { ...query, settings: query.data ?? STUDIO_DEFAULTS, save };
}
